/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import PostCard from '@/components/PostCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Send } from 'lucide-react';
import { CATEGORY_META, timeAgo, initials } from '@/lib/utils-cat';
import { toast } from 'sonner';

const PostDetailPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [pRes, cRes] = await Promise.all([
          api.get(`/posts/${id}`),
          api.get(`/posts/${id}/comments`),
        ]);
        setPost(pRes.data);
        setComments(cRes.data);
      } catch {
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const res = await api.post(`/posts/${id}/comments`, { content: text.trim() });
      setComments((prev) => [...prev, res.data]);
      setText('');
      setPost((prev) => prev ? { ...prev, comment_count: (prev.comment_count || 0) + 1 } : prev);
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setPosting(false);
    }
  };

  if (loading) return <div className="text-muted-foreground text-sm py-8">{t('loading')}</div>;
  if (!post) return null;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="post-detail-back">
        <ArrowLeft className="h-4 w-4 mr-1" /> {t('feed')}
      </Button>
      <PostCard post={post} onChanged={(p) => setPost({ ...post, ...p })} onDeleted={() => navigate('/feed')} compact />

      <div className="bg-white border border-border rounded-2xl p-4">
        <div className="font-display font-semibold text-sm mb-3">{t('comments')} ({comments.length})</div>
        <form onSubmit={submit} className="flex items-center gap-2" data-testid="comment-form">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('addComment')} data-testid="comment-input" />
          <Button type="submit" size="icon" disabled={posting || !text.trim()} data-testid="comment-submit-button">
            <Send className="h-4 w-4" />
          </Button>
        </form>
        <div className="mt-4 space-y-3">
          {comments.length === 0 && (
            <div className="text-xs text-muted-foreground py-2">—</div>
          )}
          {comments.map(c => {
            const cat = CATEGORY_META[c.author?.category];
            return (
              <div key={c.id} className="flex items-start gap-2" data-testid={`comment-${c.id}`}>
                <Avatar className="h-8 w-8">
                  {c.author?.avatar_base64 && <AvatarImage src={c.author.avatar_base64} />}
                  <AvatarFallback className="text-[10px]" style={{ backgroundColor: cat?.soft, color: cat?.textOnSoft }}>
                    {initials(c.author?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 bg-secondary/60 rounded-2xl px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold">{c.author?.full_name}</span>
                    <span className="text-[10px] text-muted-foreground">{timeAgo(c.created_at, t)}</span>
                  </div>
                  <div className="text-sm mt-0.5 whitespace-pre-wrap">{c.content}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PostDetailPage;
